<script>
  /**
   * Drag-and-resize placement of a signature box over a rendered PDF page.
   *
   * The box is kept in **natural image pixels** — the pixel grid of the PNG the
   * backend rendered — not in CSS pixels. The two differ whenever the image is
   * scaled to fit its container, which is almost always, and a box measured in
   * CSS pixels would land somewhere else on the page as soon as the window is
   * resized. Natural pixels are also exactly what the backend's
   * `imageRectToPdfPoints` expects, so nothing has to be re-derived on the way
   * out.
   *
   * The y axis here is the browser's (origin top-left). The flip into PDF user
   * space happens server-side, deliberately in one place.
   */
  import { Move } from 'lucide-svelte';

  /** @type {string} Source of the rendered page image. */
  export let imageUrl;
  /** @type {{width: number, height: number}} Page geometry in PDF points. */
  export let pageSize;
  /** @type {{x: number, y: number, width: number, height: number}} Box in natural image pixels. */
  export let rect;
  /**
   * @type {{width: number, height: number}|null}
   * Intrinsic size of the rendered page image, reported back to the parent.
   *
   * Bound rather than looked up from the DOM: `rect` is meaningless without the
   * pixel grid it was measured against, so the two must travel together to the
   * server. A `querySelector` for the image would work until the day a second
   * placer is on the page.
   */
  export let naturalSize = null;
  /** @type {boolean} Whether interaction is suspended. */
  export let disabled = false;

  /** Smallest box we allow, in natural pixels — below this it cannot be grabbed. */
  const MIN_SIZE_PX = 24;

  let imageEl;
  let naturalWidth = 0;
  let naturalHeight = 0;
  let displayWidth = 0;
  let displayHeight = 0;
  let loaded = false;

  /** Active gesture: null, or { mode, startX, startY, origin }. */
  let gesture = null;

  /** Natural pixels per CSS pixel. 1 until the image reports its size. */
  $: scale = displayWidth > 0 && naturalWidth > 0 ? naturalWidth / displayWidth : 1;

  /** Points per natural pixel, for the readout. */
  $: pointsPerPixel = naturalWidth > 0 ? pageSize.width / naturalWidth : 0;

  /**
   * Record the image's intrinsic size once it is decoded, and seed a default
   * box if the caller has not placed one yet.
   */
  function onImageLoad() {
    naturalWidth = imageEl.naturalWidth;
    naturalHeight = imageEl.naturalHeight;
    naturalSize = { width: naturalWidth, height: naturalHeight };
    measure();
    loaded = true;

    if (!rect) {
      // 164x45 points is the provider's own default widget size; converted here
      // so the initial box matches what an unplaced signature would look like.
      const width = 164 / (pageSize.width / naturalWidth);
      const height = 45 / (pageSize.height / naturalHeight);
      rect = {
        x: Math.round(naturalWidth - width - naturalWidth * 0.08),
        y: Math.round(naturalHeight - height - naturalHeight * 0.08),
        width: Math.round(width),
        height: Math.round(height),
      };
    }
  }

  /** Re-read the rendered size, which changes with the container. */
  function measure() {
    if (!imageEl) return;
    displayWidth = imageEl.clientWidth;
    displayHeight = imageEl.clientHeight;
  }

  /**
   * Clamp a box so it stays fully on the page.
   * @param {{x: number, y: number, width: number, height: number}} next Proposed box
   * @returns {{x: number, y: number, width: number, height: number}}
   */
  function clamp(next) {
    const width = Math.min(Math.max(next.width, MIN_SIZE_PX), naturalWidth);
    const height = Math.min(Math.max(next.height, MIN_SIZE_PX), naturalHeight);
    return {
      width,
      height,
      x: Math.min(Math.max(next.x, 0), naturalWidth - width),
      y: Math.min(Math.max(next.y, 0), naturalHeight - height),
    };
  }

  /**
   * Begin a move or resize gesture.
   * @param {PointerEvent} event Pointer event
   * @param {'move'|'resize'} mode Gesture kind
   */
  function startGesture(event, mode) {
    if (disabled || !loaded) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    gesture = { mode, startX: event.clientX, startY: event.clientY, origin: { ...rect } };
  }

  /**
   * Apply a gesture's delta.
   * @param {PointerEvent} event Pointer event
   */
  function moveGesture(event) {
    if (!gesture) return;
    // CSS-pixel deltas are converted to natural pixels, so dragging one screen
    // pixel moves the box by one screen pixel regardless of the zoom level.
    const dx = (event.clientX - gesture.startX) * scale;
    const dy = (event.clientY - gesture.startY) * scale;

    rect = clamp(gesture.mode === 'move'
      ? { ...gesture.origin, x: gesture.origin.x + dx, y: gesture.origin.y + dy }
      : { ...gesture.origin, width: gesture.origin.width + dx, height: gesture.origin.height + dy });
  }

  /** End the gesture and snap to whole pixels. */
  function endGesture() {
    if (!gesture) return;
    gesture = null;
    rect = {
      x: Math.round(rect.x),
      y: Math.round(rect.y),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    };
  }

  /**
   * Nudge the box with the arrow keys, so placement does not require a mouse.
   * @param {KeyboardEvent} event Key event
   */
  function onKeyDown(event) {
    if (disabled || !loaded) return;
    const step = event.shiftKey ? 10 : 1;
    const moves = {
      ArrowLeft: { x: -step }, ArrowRight: { x: step },
      ArrowUp: { y: -step }, ArrowDown: { y: step },
    };
    const delta = moves[event.key];
    if (!delta) return;
    event.preventDefault();
    rect = clamp({ ...rect, x: rect.x + (delta.x || 0), y: rect.y + (delta.y || 0) });
  }
</script>

<svelte:window on:resize={measure} />

<div class="relative inline-block max-w-full select-none">
  <img
    bind:this={imageEl}
    src={imageUrl}
    alt="Anteprima pagina"
    class="max-w-full h-auto block border-2 border-gray-300 dark:border-gray-700"
    onload={onImageLoad}
    draggable="false"
  />

  {#if loaded && rect}
    <!-- Positioned as a percentage of the image, so it tracks any rescaling
         without a second measure pass. The wrapper carries only geometry; the
         two <button>s inside carry the interaction, which keeps both of them
         focusable and operable from the keyboard for free. -->
    <div
      class="absolute border-2 border-primary-500 bg-primary-500/15"
      style="left: {(rect.x / naturalWidth) * 100}%;
             top: {(rect.y / naturalHeight) * 100}%;
             width: {(rect.width / naturalWidth) * 100}%;
             height: {(rect.height / naturalHeight) * 100}%;"
    >
      <button
        type="button"
        class="absolute inset-0 flex items-center justify-center w-full h-full bg-transparent border-0 p-0 {disabled ? 'cursor-default' : 'cursor-move'}"
        aria-label="Sposta la firma. Usa le frecce per spostarla, Maiusc+frecce per passi da 10 pixel."
        {disabled}
        onpointerdown={(e) => startGesture(e, 'move')}
        onpointermove={moveGesture}
        onpointerup={endGesture}
        onpointercancel={endGesture}
        onkeydown={onKeyDown}
      >
        <Move size={16} class="text-primary-600 opacity-70 pointer-events-none" />
      </button>

      {#if !disabled}
        <!-- Bottom-right resize handle. -->
        <button
          type="button"
          class="absolute -right-1.5 -bottom-1.5 w-4 h-4 p-0 bg-primary-500 border-2 border-white cursor-nwse-resize"
          aria-label="Ridimensiona la firma"
          onpointerdown={(e) => startGesture(e, 'resize')}
          onpointermove={moveGesture}
          onpointerup={endGesture}
          onpointercancel={endGesture}
        ></button>
      {/if}
    </div>
  {/if}
</div>

{#if loaded && rect && pointsPerPixel > 0}
  <p class="mt-2 text-xs text-gray-500 dark:text-gray-400 font-mono">
    {Math.round(rect.width * pointsPerPixel)} × {Math.round(rect.height * pointsPerPixel)} pt
    &middot; x {Math.round(rect.x * pointsPerPixel)} pt da sinistra
    &middot; y {Math.round(pageSize.height - (rect.y + rect.height) * pointsPerPixel)} pt dal basso
  </p>
{/if}
